import { z } from 'zod';
import { NMS_DEL_CHECKIN, PROTOCOLO_12_MESES, ULTIMO_DIA_DEL_PROTOCOLO } from './protocolo.js';
/**
 * Patrones de intensidad del seguimiento · UC-M08.
 *
 * El protocolo no fija una cadencia única y lo dice de frente: en el hito M3 "el
 * UA define modalidad M4–M6: quincenal, mensual o alta parcial", y en D+120
 * "definir frecuencia M5–M6: quincenal o mensual según evolución".
 * `seguimiento.html` lo llama **intensidad variable** y lo pone como principio de
 * diseño: "el motor de reglas puede ajustar la intensidad según señales,
 * evolución y criterio clínico".
 *
 * Un patrón cambia **sólo los check-ins que todavía no salieron**. Las
 * evaluaciones y las teleconsultas no se tocan: son hitos del protocolo, no
 * intensidad, y moverlas rompería la serie longitudinal que compara a un paciente
 * con los demás.
 *
 * El patrón `semanal` es, de paso, cómo se alcanza la cadencia que el encabezado
 * del protocolo describe y que su línea de tiempo no itemiza.
 */
export const PATRONES_DE_CADENCIA = ['protocolo', 'semanal', 'quincenal', 'mensual'];
export const patronDeCadenciaSchema = z.enum(PATRONES_DE_CADENCIA);
/** Cada cuántos días cae un check-in con este patrón. `null` = el del protocolo. */
const DIAS_ENTRE_CHECKINS = Object.freeze({
    protocolo: null,
    semanal: 7,
    quincenal: 14,
    mensual: 30,
});
/**
 * Los check-ins que un patrón genera entre dos días.
 *
 * **No pisa un día que ya tiene evaluación o teleconsulta.** El protocolo trata
 * esos días como el contacto de esa semana —la evaluación de D+28 "es" el
 * check-in de la semana 4, con su cuestionario ampliado—, así que agregarle
 * encima un microcheck sería pedirle al paciente que conteste dos veces el mismo
 * día.
 *
 * El formulario sale del que el protocolo usa en esa altura del programa: la
 * batería del primer semestre hasta D+180 y la mensual del segundo después. Y
 * cada check-in lleva el árbol básico del NMS, igual que los del protocolo (D-02).
 */
export function checkInsDelPatron(patron, desdeDia, hastaDia = ULTIMO_DIA_DEL_PROTOCOLO) {
    const cada = DIAS_ENTRE_CHECKINS[patron];
    if (cada === null) {
        return PROTOCOLO_12_MESES.filter((item) => item.clase === 'microcheck' && item.dia >= desdeDia && item.dia <= hastaDia).map((item) => ({ ...item }));
    }
    const ocupados = new Set(PROTOCOLO_12_MESES.filter((item) => item.clase !== 'microcheck').map((item) => item.dia));
    const items = [];
    // Se arranca en el primer múltiplo del intervalo que caiga en el rango, para
    // que el patrón no dependa del día en que el profesional lo cambió: dos
    // pacientes con el mismo patrón tienen los check-ins los mismos días.
    const primero = Math.ceil(Math.max(desdeDia, cada) / cada) * cada;
    for (let dia = primero; dia <= hastaDia; dia += cada) {
        if (ocupados.has(dia))
            continue;
        items.push({
            codigo: `checkin-p${String(dia).padStart(3, '0')}`,
            clase: 'microcheck',
            dia,
            hito: `D+${dia}`,
            formularioId: formularioDelDia(dia),
            instrumentos: NMS_DEL_CHECKIN,
            obligatorio: true,
            descripcion: `Check-in del día ${dia} · patrón ${patron}`,
        });
    }
    return items;
}
/**
 * Qué batería corresponde a esa altura del programa.
 *
 * El corte es D+180, que es donde el protocolo cierra el primer semestre y
 * cambia el foco: de recuperación funcional a calidad de vida, participación
 * social y autonomía.
 */
function formularioDelDia(dia) {
    return dia <= 180 ? 'microcheck-semanal' : 'microcheck-mensual';
}
//# sourceMappingURL=cadencia.js.map